import React, { useState, useContext } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FiPercent, FiEdit2 } from 'react-icons/fi';
import axios from 'axios';
import Swal from 'sweetalert2';
import AppContext from '../../../../context/AppContext';
import '../../../../assets/css/superadmin/dashboard/commitionrate/Commitionrate.css';

const CommissionRateManager = () => {
  const { baseUrl, authToken, loggedInAdmin } = useContext(AppContext);
  const isModerator = loggedInAdmin?.issuper === 'moderator';

  const [showModal, setShowModal] = useState(false);

  // ── Rental ──
  const [currentRate, setCurrentRate] = useState(null);
  const [newRate, setNewRate] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  // ── Mechanic ──
  const [currentMechanicRate, setCurrentMechanicRate] = useState(null);
  const [newMechanicRate, setNewMechanicRate] = useState('');
  const [mechanicLastUpdated, setMechanicLastUpdated] = useState(null);

  const fetchCurrentRates = () => {
    axios.get(`${baseUrl}/superadmin/commission/getCurrentRates`, {
      headers: { Authorization: `Bearer ${authToken}` },
    }).then((res) => {
      if (res.data.success) {
        setCurrentRate(res.data.rentalRate);
        setLastUpdated(res.data.rentalUpdatedAt);
        setCurrentMechanicRate(res.data.mechanicRate);
        setMechanicLastUpdated(res.data.mechanicUpdatedAt);
      }
    }).catch((err) => console.log(err));
  };

  // ── Load the real saved rates as soon as the page mounts, not just after a save ──
  React.useEffect(() => {
    fetchCurrentRates();
  }, []);

  const handleOpenModal = () => {
    setShowModal(true);
    setNewRate('');
    setNewMechanicRate('');
    fetchCurrentRates(); // refresh in case it changed elsewhere
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setNewRate('');
    setNewMechanicRate('');
  };

  // ── Rental: Set / Update ──
  const handleRentalRate = async () => {
    if (!newRate || isNaN(newRate) || newRate < 0 || newRate > 100) {
      Swal.fire({ icon: 'warning', title: 'Invalid Rate', text: 'Please enter a valid rate between 0 and 100.', confirmButtonColor: '#FDB714' });
      return;
    }

    const formData = new FormData();
    formData.append('rate', newRate);

    const endpoint = currentRate !== null ? 'updateRentalCommission' : 'setRentalCommission';

    await axios.post(`${baseUrl}/superadmin/commission/${endpoint}`, formData, {
      headers: { Authorization: `Bearer ${authToken}` },
    }).then((res) => {
      if (res.data.success) {
        setCurrentRate(res.data.rate);
        setLastUpdated(res.data.updated_at);
        setNewRate('');
        Swal.fire({ icon: 'success', title: 'Saved!', text: res.data.message, confirmButtonColor: '#FDB714' });
      } else {
        Swal.fire({ icon: 'error', title: 'Failed', text: res.data.message, confirmButtonColor: '#FDB714' });
      }
    }).catch((err) => {
      console.log(err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Something went wrong. Please try again.', confirmButtonColor: '#FDB714' });
    });
  };

  // ── Mechanic: Set / Update ──
  const handleMechanicRate = async () => {
    if (!newMechanicRate || isNaN(newMechanicRate) || newMechanicRate < 0) {
      Swal.fire({ icon: 'warning', title: 'Invalid Amount', text: 'Please enter a valid amount.', confirmButtonColor: '#FDB714' });
      return;
    }

    const formData = new FormData();
    formData.append('rate', newMechanicRate);
    await axios.post(`${baseUrl}/superadmin/commission/setMechanicCommission`, formData, {
      headers: { Authorization: `Bearer ${authToken}` },
    }).then((res) => {
      if (res.data.success) {
        setCurrentMechanicRate(res.data.rate);
        setMechanicLastUpdated(res.data.updated_at);
        setNewMechanicRate('');
        Swal.fire({ icon: 'success', title: 'Saved!', text: res.data.message, confirmButtonColor: '#FDB714' });
      } else {
        Swal.fire({ icon: 'error', title: 'Failed', text: res.data.message, confirmButtonColor: '#FDB714' });
      }
    }).catch((err) => {
      console.log(err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Something went wrong. Please try again.', confirmButtonColor: '#FDB714' });
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <>
      <button className="commission-rate-button" onClick={handleOpenModal}>
        <FiPercent className="commission-icon" />
        <span>Commission Rate</span>
      </button>

      <Modal show={showModal} onHide={handleCloseModal} centered className="commission-modal">
        <Modal.Header closeButton className="commission-modal-header">
          <Modal.Title className="commission-modal-title">
            <FiPercent className="modal-title-icon" />
            Commission Rate Management
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="commission-modal-body">

          {/* Rental */}
          <div className="current-rate-display">
            <label className="rate-label">Current Commission Rate For Rental</label>
            <div className="rate-value-box">
              <span className="rate-value">
                {currentRate !== null ? `${currentRate}%` : '0 '}
              </span>
              <span className="rate-update-info">
                {currentRate !== null ? `Last updated: ${formatDate(lastUpdated)}` : 'No rate set yet'}
              </span>
            </div>
          </div>

          <div className="rate-input-group">
            <label className="input-label">New Commission Rate For Rental (%)</label>
            <div className="input-wrapper">
              <input
                type="number"
                className="rate-input"
                placeholder="Enter new rate (0-100)"
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
                min="0"
                max="100"
                disabled={isModerator}
              />
              <span className="input-icon">%</span>
            </div>
          </div>

          <div style={{ textAlign: 'right', marginBottom: '1.5rem' }}>
            {isModerator ? (
              <span style={{ fontSize: 12, color: '#999' }}>View only — only a superadmin can change this.</span>
            ) : (
              <Button variant="primary" onClick={handleRentalRate} className="update-button">
                <FiEdit2 className="button-icon" />
                {currentRate !== null ? 'Update Rental Rate' : 'Set Rental Rate'}
              </Button>
            )}
          </div>

          <hr style={{ borderColor: '#E5E5E5', margin: '0 0 1.5rem 0' }} />

          {/* Mechanic */}
          <div className="current-rate-display">
            <label className="rate-label">Current Commission Rate For Mechanic</label>
            <div className="rate-value-box">
              <span className="rate-value">
                {currentMechanicRate !== null ? `Rs. ${currentMechanicRate}` : '0'}
              </span>
              <span className="rate-update-info">
                {currentMechanicRate !== null ? `Last updated: ${formatDate(mechanicLastUpdated)}` : 'No rate set yet'}
              </span>
            </div>
          </div>

          <div className="rate-input-group">
            <label className="input-label">New Commission Rate For Mechanic (Rs.)</label>
            <div className="input-wrapper">
              <input
                type="number"
                className="rate-input"
                placeholder="Enter new fixed amount"
                value={newMechanicRate}
                onChange={(e) => setNewMechanicRate(e.target.value)}
                min="0"
                disabled={isModerator}
              />
              <span className="input-icon">Rs</span>
            </div>
          </div>

          <div style={{ textAlign: 'right' }}>
            {isModerator ? (
              <span style={{ fontSize: 12, color: '#999' }}>View only — only a superadmin can change this.</span>
            ) : (
              <Button variant="primary" onClick={handleMechanicRate} className="update-button">
                <FiEdit2 className="button-icon" />
                {currentMechanicRate !== null ? 'Update Mechanic Rate' : 'Set Mechanic Rate'}
              </Button>
            )}
          </div>

        </Modal.Body>

        <Modal.Footer className="commission-modal-footer">
          <Button variant="secondary" onClick={handleCloseModal} className="cancel-button">
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CommissionRateManager;